import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

// Source images:
//   /lion-almost-open.jpeg  → pre-launch announcement art (current branding through 2026-12-28)
//   /lion-logo.jpeg         → same source we crop for the lion-only "mark"
// After the auto-swap date (soft_launch_date + 180 days), the "full" variant flips
// from the announcement art to a clean lion mark + post-launch tagline.

const PRE_LAUNCH_SRC = "/lion-almost-open.jpeg";
const MARK_SRC = "/lion-logo.jpeg";

// Cache the launch settings across all LionLogo instances so we don't refetch.
let _launchCache = null;
let _launchPromise = null;
async function getLaunchSettings() {
  if (_launchCache) return _launchCache;
  if (!_launchPromise) {
    _launchPromise = api
      .get("/launch-settings")
      .then((r) => {
        _launchCache = r.data;
        return _launchCache;
      })
      .catch(() => ({ logo_swap_active: false, post_launch_tagline: "Shielded By Fire. Guarded By Spirit." }));
  }
  return _launchPromise;
}

function LionMark({ size, className }) {
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${MARK_SRC})`,
        backgroundRepeat: "no-repeat",
        backgroundColor: "#000",
        backgroundSize: "auto 260%",
        backgroundPosition: "center 18%",
        borderRadius: "9999px",
      }}
      role="img"
      aria-label="Lyon's Den Protection lion mark"
      data-testid="lion-logo"
    />
  );
}

export const LionLogo = ({ size = 56, className = "", variant = "mark" }) => {
  const [phase, setPhase] = useState(() => ({
    logo_swap_active: false,
    post_launch_tagline: "Shielded By Fire. Guarded By Spirit.",
  }));

  useEffect(() => {
    let mounted = true;
    getLaunchSettings().then((d) => {
      if (mounted) setPhase(d);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Mark variant is unchanged across phases — it's always the lion-only emblem.
  if (variant === "mark") {
    return <LionMark size={size} className={className} />;
  }

  // Full variant: pre-launch shows the announcement image as-is.
  if (!phase.logo_swap_active) {
    return (
      <img
        src={PRE_LAUNCH_SRC}
        alt="Lyon's Den Protection — The Den Is Almost Open"
        style={{ width: size, height: "auto" }}
        className={className}
        data-testid="lion-logo-full"
      />
    );
  }

  // Post-launch: clean lion mark + brand wordmark + new tagline.
  const markSize = Math.round(size * 0.65);
  return (
    <div
      className={`flex flex-col items-center text-center ${className}`}
      style={{ width: size }}
      data-testid="lion-logo-full-postlaunch"
    >
      <LionMark size={markSize} />
      <div
        className="font-serif tracking-[0.18em] text-[#8B0000] mt-5"
        style={{ fontSize: Math.max(18, Math.round(size * 0.085)) }}
      >
        LYON'S DEN PROTECTION
      </div>
      <div
        className="font-serif italic text-[#D4AF37] mt-2"
        style={{ fontSize: Math.max(12, Math.round(size * 0.05)) }}
        data-testid="lion-logo-tagline"
      >
        {phase.post_launch_tagline || "Shielded By Fire. Guarded By Spirit."}
      </div>
    </div>
  );
};

export default LionLogo;
